import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import VehiclesSubNav from "./VehiclesSubNav";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { Wrench, CheckCircle } from "lucide-react";

export default function Services() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [tab, setTab] = useState("upcoming");

  const load = async () => {
    try {
      const params = tab === "upcoming" ? { upcoming: true, days: 120 } : {};
      const { data } = await api.get("/vehicle-services", { params });
      setItems(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  useEffect(() => {
    if (user) {
      load();
      api.get("/vehicles").then((r) => {
        const map = {};
        (r.data || []).forEach((v) => { map[v.id] = v; });
        setVehicles(map);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;

  const markDone = async (srv) => {
    try {
      await api.patch(`/vehicle-services/${srv.id}`, { completed: true });
      toast.success(t("vsvc.markCompleted"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="mb-6">
          <div className="label-tech">{t("veh.label")}</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("vsvc.title")}</h1>
        </div>

        <VehiclesSubNav />

        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab("upcoming")} className={`px-3 py-1.5 text-xs font-mono-tech uppercase tracking-[0.1em] border ${tab === "upcoming" ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]" : "border-[var(--ink-200)]"}`} data-testid="vsvc-tab-upcoming">{t("vsvc.upcoming")}</button>
          <button onClick={() => setTab("all")} className={`px-3 py-1.5 text-xs font-mono-tech uppercase tracking-[0.1em] border ${tab === "all" ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]" : "border-[var(--ink-200)]"}`} data-testid="vsvc-tab-all">{t("vsvc.all")}</button>
        </div>

        <div className="border border-[var(--ink-200)] bg-[var(--bone)]" data-testid="services-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 label-tech border-b border-[var(--ink-200)]">
            <div className="col-span-3">{t("vins.date")}</div>
            <div className="col-span-3">{t("veh.regnr")}</div>
            <div className="col-span-3">{t("vsvc.type")}</div>
            <div className="col-span-3 text-right">{t("vsvc.completed")}</div>
          </div>
          {items.length === 0 ? (
            <div className="p-10 label-tech text-center">{t("vsvc.empty")}</div>
          ) : items.map((s) => {
            const v = vehicles[s.vehicle_id];
            return (
              <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[var(--ink-200)] text-sm items-center" data-testid={`vsvc-row-${s.id}`}>
                <div className="col-span-3 font-mono-tech">{s.date?.slice(0, 10)}</div>
                <div className="col-span-3">
                  <Link to={`/vehicles/${s.vehicle_id}`} className="font-mono-tech underline decoration-dotted">{v?.regnr || s.vehicle_id.slice(0, 8)}</Link>
                </div>
                <div className="col-span-3 inline-flex items-center gap-1"><Wrench size={12} /> {t(`vsvc.type.${s.service_type || "regular"}`)}</div>
                <div className="col-span-3 flex justify-end">
                  {s.completed ? (
                    <span className="inline-flex items-center gap-1 text-[var(--moss)]"><CheckCircle size={12} /> {t("vsvc.completed")}</span>
                  ) : (
                    <button onClick={() => markDone(s)} className="btn-ghost text-xs" data-testid={`vsvc-done-${s.id}`}>{t("vsvc.markCompleted")}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
